import { useState, useRef, useEffect, useCallback } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Play, Square, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export default function DroneTool({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [note, setNote] = useState('A');
    const [octave, setOctave] = useState(2);
    const [isPlaying, setIsPlaying] = useState(false);
    const [saving, setSaving] = useState(false);
    
    const audioContextRef = useRef(null);
    const nodesRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (nodesRef.current) {
                try {
                    nodesRef.current.oscillator1?.stop();
                    nodesRef.current.oscillator2?.stop();
                } catch {}
                nodesRef.current = null;
            }
        };
    }, []);

    const getFrequency = useCallback((noteName, oct) => {
        const noteIndex = NOTES.indexOf(noteName);
        const a4 = 440;
        const halfSteps = noteIndex - 9 + (oct - 4) * 12;
        return a4 * Math.pow(2, halfSteps / 12);
    }, []);

    const startDrone = useCallback(() => {
        if (nodesRef.current) {
            try {
                nodesRef.current.oscillator1?.stop();
                nodesRef.current.oscillator2?.stop();
            } catch {}
            nodesRef.current = null;
        }

        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioContextRef.current;
        
        if (ctx.state === 'suspended') {
            ctx.resume();
        }

        const freq = getFrequency(note, octave);

        const makeDistortionCurve = (amount) => {
            const k = amount;
            const samples = 44100;
            const curve = new Float32Array(samples);
            const deg = Math.PI / 180;
            for (let i = 0; i < samples; ++i) {
                const x = (i * 2) / samples - 1;
                curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
            }
            return curve;
        };

        const osc1 = ctx.createOscillator();
        osc1.type = 'sawtooth';
        osc1.frequency.value = freq;

        const osc2 = ctx.createOscillator();
        osc2.type = 'sine';
        osc2.frequency.value = freq / 2;

        const distortion = ctx.createWaveShaper();
        distortion.curve = makeDistortionCurve(8);
        distortion.oversample = '4x';

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 800;
        filter.Q.value = 2;

        const osc1Gain = ctx.createGain();
        osc1Gain.gain.value = 0.4;

        const osc2Gain = ctx.createGain();
        osc2Gain.gain.value = 0.5;

        const masterGain = ctx.createGain();
        masterGain.gain.setValueAtTime(0, ctx.currentTime);
        masterGain.gain.linearRampToValueAtTime(0.35, ctx.currentTime + 0.5);

        osc1.connect(osc1Gain);
        osc1Gain.connect(distortion);
        distortion.connect(filter);
        filter.connect(masterGain);

        osc2.connect(osc2Gain);
        osc2Gain.connect(masterGain);

        masterGain.connect(ctx.destination);

        osc1.start();
        osc2.start();

        nodesRef.current = {
            oscillator1: osc1,
            oscillator2: osc2,
            masterGain,
            filter
        };

        setIsPlaying(true);
    }, [note, octave, getFrequency]);

    const stopDrone = useCallback(() => {
        if (nodesRef.current && audioContextRef.current) {
            const { masterGain, oscillator1, oscillator2 } = nodesRef.current;
            const ctx = audioContextRef.current;
            
            masterGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
            
            setTimeout(() => {
                try {
                    oscillator1?.stop();
                    oscillator2?.stop();
                } catch {}
                nodesRef.current = null;
            }, 250);
        }
        setIsPlaying(false);
    }, []);

    // Update frequency when note/octave changes while playing
    useEffect(() => {
        if (isPlaying && nodesRef.current) {
            const freq = getFrequency(note, octave);
            nodesRef.current.oscillator1.frequency.value = freq;
            nodesRef.current.oscillator2.frequency.value = freq / 2;
        }
    }, [note, octave, isPlaying, getFrequency]);

    const handleSave = async () => {
        stopDrone();
        setSaving(true);
        try {
            const freq = getFrequency(note, octave);
            const fragment = await addFragment(songId, {
                type: 'drone',
                label: 'Drone Reference',
                note: `${note}${octave}`,
                frequency: Math.round(freq * 100) / 100
            });
            toast.success('Drone saved');
            onComplete(fragment?.id);
        } catch (error) {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5 max-w-sm mx-auto">
            {/* Note display */}
            <div className={`text-center p-6 rounded bg-muted/30 ${isPlaying ? 'drone-active' : ''}`}>
                <span className="text-4xl font-medium">{note}{octave}</span>
                <p className="text-sm text-muted-foreground mt-1">
                    {Math.round(getFrequency(note, octave))} Hz
                </p>
            </div>

            {/* Note selector */}
            <div className="space-y-2">
                <Label className="text-xs font-medium">Note</Label>
                <div className="grid grid-cols-6 gap-1.5">
                    {NOTES.map((n) => (
                        <button
                            key={n}
                            onClick={() => setNote(n)}
                            className={`p-2 text-sm rounded transition-all ${
                                note === n
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                        >
                            {n}
                        </button>
                    ))}
                </div>
            </div>

            {/* Octave slider */}
            <div className="space-y-2">
                <div className="flex justify-between">
                    <Label className="text-xs font-medium">Octave</Label>
                    <span className="text-xs text-muted-foreground">{octave}</span>
                </div>
                <Slider
                    value={[octave]}
                    onValueChange={([v]) => setOctave(v)}
                    min={1}
                    max={4}
                    step={1}
                />
            </div>

            {/* Play/Stop */}
            <Button
                onClick={isPlaying ? stopDrone : startDrone}
                variant={isPlaying ? 'secondary' : 'default'}
                className="w-full h-11"
            >
                {isPlaying ? (
                    <><Square className="w-4 h-4 mr-2" /> Stop Drone</>
                ) : (
                    <><Play className="w-4 h-4 mr-2" /> Play Drone</>
                )}
            </Button>

            {/* Save */}
            <Button
                onClick={handleSave}
                disabled={saving}
                variant="outline"
                className="w-full h-9"
            >
                {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Check className="w-4 h-4 mr-2" />
                )}
                Save as Reference
            </Button>
        </div>
    );
}
